/**
 * Live Location Sharing Service
 *
 * Handles neighborhood-level ambient location sharing between connections.
 * - Updates the current user's location on app open/foreground
 * - Fetches connection neighborhoods via Supabase RPC
 * - Manages the sharing toggle (localStorage + Supabase)
 *
 * Uses Radar reverse geocode (address only, no venue search).
 * See docs/features/live-location-sharing.md for full design.
 */

import { supabase } from '../supabase';
import { getCurrentLocation } from './locationService';
import { reverseGeocode } from './providers/radarProvider';
import { getCityAbbreviation } from '../../utils/locationFormatters';
import { isNewExperience } from '../../utils/newExperience';

const SHARING_ENABLED_KEY = 'location_sharing_enabled';
const LAST_UPDATED_KEY = 'location_last_updated';
const UPDATE_GUARD_MS = 5 * 60 * 1000; // 5 minutes — prevent rapid re-geocoding

// ─── Toggle ────────────────────────────────────────────────────────────────

/**
 * Check if location sharing is enabled (instant, from localStorage).
 * @returns {boolean}
 */
export const getLocationSharingEnabled = () => {
  return localStorage.getItem(SHARING_ENABLED_KEY) === 'true';
};

/**
 * Toggle location sharing on/off.
 * Updates localStorage immediately (for instant UI), then syncs to Supabase.
 *
 * @param {string} userId - profile_user_id
 * @param {boolean} enabled
 */
export const setLocationSharing = async (userId, enabled) => {
  localStorage.setItem(SHARING_ENABLED_KEY, String(enabled));

  const { error } = await supabase.from('user_locations').upsert(
    {
      user_id: userId,
      sharing_enabled: enabled,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id' }
  );

  if (error) {
    console.error('[LIVE_LOCATION] Failed to update sharing:', error.message);
  }
};

// ─── Location update ───────────────────────────────────────────────────────

/**
 * Update the current user's live location.
 * Gets GPS → reverse geocodes to neighborhood → upserts to user_locations.
 *
 * Guarded: skips if last update was < 5 minutes ago.
 *
 * @param {string} userId - profile_user_id
 * @param {Object} [options]
 * @param {{latitude: number, longitude: number}} [options.coords] - a fix the caller already
 *   holds (the Connect screen's) — skips a second GPS read
 * @returns {Promise<{ok: boolean, error?: string}>} ok=true if updated or skipped (fresh), ok=false on real failure
 */
export const updateLiveLocation = async (userId, { coords: knownCoords } = {}) => {
  // 5-minute guard — location is already fresh, not a failure
  const lastUpdate = localStorage.getItem(LAST_UPDATED_KEY);
  if (lastUpdate && Date.now() - Number(lastUpdate) < UPDATE_GUARD_MS) {
    console.log('[LIVE_LOCATION] 5-min guard — skipped');
    return { ok: true };
  }

  try {
    // GPS coordinates — the caller's fix when it has one
    const coords = knownCoords || (await getCurrentLocation());

    // Reverse geocode (address only, no venue search)
    const address = await reverseGeocode(coords.latitude, coords.longitude);

    // Build neighborhood display from fallback chain:
    // neighborhood + city > city only > state > skip
    const neighborhood = address?.neighborhood || null;
    const city = address?.city ? getCityAbbreviation(address.city) : null;
    const state = address?.stateCode || address?.state || null;

    // Determine what to store
    let displayNeighborhood = neighborhood;
    let displayCity = city;

    if (!neighborhood && !city && !state) {
      // Nothing useful from geocode — don't update
      console.warn('[LIVE_LOCATION] No neighborhood/city/state from geocode, skipping');
      return { ok: false, error: 'no_location_data' };
    }

    if (!neighborhood && !city && state) {
      // Only state available — use it as the city-level display
      displayCity = state;
    }

    // Upsert to Supabase
    const { error } = await supabase.from('user_locations').upsert(
      {
        user_id: userId,
        neighborhood: displayNeighborhood,
        city: displayCity,
        lat: coords.latitude,
        lng: coords.longitude,
        // The place's state and country, from this same lookup (location is a
        // primitive: Places tells Falmouth, MA from Falmouth, ME). New-experience
        // only: the columns (051) reach prod with the flip.
        ...(isNewExperience() && {
          state_code: address?.stateCode || null,
          country_code: address?.countryCode || null,
        }),
        // No sharing_enabled here: storing a location is not sharing it.
        // setLocationSharing owns that switch (the enable path calls it right
        // after this); a new row defaults to false — kept, not shown
        // (signup-connect step 5: the Connect screen keeps location for
        // everyone without turning on friends-see-my-neighbourhood).
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    );

    if (error) {
      console.error('[LIVE_LOCATION] Failed to upsert location:', error.message);
      return { ok: false, error: 'db_error' };
    }

    localStorage.setItem(LAST_UPDATED_KEY, String(Date.now()));
    console.log(
      `[LIVE_LOCATION] Updated: ${displayNeighborhood || '(no neighborhood)'}, ${displayCity || '(no city)'} | coords: ${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`
    );
    return { ok: true };
  } catch (error) {
    console.error('[LIVE_LOCATION] Update failed:', error.message);
    return { ok: false, error: error.code === 1 ? 'permission_denied' : 'gps_error' };
  }
};

// ─── Fetch connection locations ────────────────────────────────────────────

/**
 * Format a neighborhood + city into a display string.
 * "Venice Beach, LA" or "LA" or "Venice Beach" depending on what's available.
 */
export const formatNeighborhoodDisplay = (neighborhood, city) => {
  if (neighborhood && city) return `${neighborhood}, ${city}`;
  return neighborhood || city || '';
};

// Freshness window for showing a stored neighbourhood — match the window peers
// see via get_connection_locations (24h since migration 047, up from 8h). A row
// older than this is stale: callers fall through to a live geocode rather than
// preview a days-old place.
export const LOCATION_FRESH_MS = 24 * 60 * 60 * 1000;

// A stored user_locations row's neighbourhood display, but only while it's still
// fresh (within LOCATION_FRESH_MS); '' when stale or missing. Shared by the share
// sheet's first-paint seed and its async refresh so both read one window.
export const freshNeighborhood = (row) =>
  row?.updated_at && Date.now() - new Date(row.updated_at).getTime() < LOCATION_FRESH_MS
    ? formatNeighborhoodDisplay(row.neighborhood, row.city)
    : '';

/**
 * Fetch live neighborhoods for all connections of the given user.
 * Uses the get_connection_locations RPC which JOINs user_locations with connections.
 *
 * @param {string} userId - profile_user_id or anon_user_id
 * @returns {Promise<Object>} Map of chatRoomId → formatted neighborhood string
 */
export const getConnectionLocations = async (userId) => {
  const { data, error } = await supabase.rpc('get_connection_locations', {
    p_user_id: userId,
  });

  // Throws on RPC error (cached-read contract) — an empty result is a normal
  // answer (nobody sharing), a failure must not look like one.
  if (error) {
    throw new Error(`[LIVE_LOCATION] getConnectionLocations failed: ${error.message}`);
  }

  if (!data || data.length === 0) return {};

  // Build map: chatRoomId → display string
  const locationMap = {};
  for (const row of data) {
    const display = formatNeighborhoodDisplay(row.neighborhood, row.city);
    if (display) {
      locationMap[row.chat_room_id] = display;
    }
  }

  return locationMap;
};

/**
 * Fetch full live-location records (with coords) for all connections — like
 * getConnectionLocations but keeps lat/lng + raw city/neighborhood, for Nearby
 * grouping. Reciprocity is enforced by the caller (only fetch when the viewer
 * is sharing).
 *
 * @param {string} userId - profile_user_id
 * @returns {Promise<Object>} chatRoomId → { neighborhood, city, lat, lng, updatedAt }
 */
export const getConnectionLocationRecords = async (userId) => {
  const { data, error } = await supabase.rpc('get_connection_locations', {
    p_user_id: userId,
  });

  // Throws on RPC error (cached-read contract) — an empty result is a normal
  // answer (nobody sharing), a failure must not look like one.
  if (error) {
    throw new Error(`[LIVE_LOCATION] getConnectionLocationRecords failed: ${error.message}`);
  }
  if (!data || data.length === 0) return {};

  const map = {};
  for (const row of data) {
    map[row.chat_room_id] = {
      neighborhood: row.neighborhood,
      city: row.city,
      lat: row.lat,
      lng: row.lng,
      updatedAt: row.updated_at,
    };
  }
  return map;
};

/**
 * Fetch the current user's own live-location row — for the Nearby viewer anchor
 * and the share-sheet neighborhood preview. Returns null if there's no row.
 *
 * @param {string} userId - profile_user_id
 * @returns {Promise<{lat, lng, city, neighborhood, sharing_enabled, updated_at}|null>}
 */
export const getMyLiveLocation = async (userId) => {
  const { data, error } = await supabase
    .from('user_locations')
    .select('lat, lng, city, neighborhood, sharing_enabled, updated_at')
    .eq('user_id', userId)
    .maybeSingle();

  // Throws on Supabase error (cached-read contract). A missing row still
  // returns null — "no location yet" is a normal answer, not a failure.
  if (error) {
    throw new Error(`[LIVE_LOCATION] getMyLiveLocation failed: ${error.message}`);
  }
  return data || null;
};

/**
 * Reconcile the local sharing flag with the DB and return the truth.
 *
 * The pin UI reads getLocationSharingEnabled() (localStorage) for instant paint,
 * but that flag can drift false while user_locations.sharing_enabled is true — a
 * fresh PWA origin, cleared storage, or because the pre-Oct-6 writer that used to
 * keep it set no longer runs (signup-connect: storing never turns on sharing).
 * Hydrating from the DB makes the owner see their real state. See
 * docs/fixes/.archived/location-share-sync.md.
 *
 * No-Fallbacks exception (2026-10-16, discussed + approved): on a read error we
 * KEEP the last local value and never force the enable prompt on a transient
 * blip — but we return the error so the caller logs it visibly (not a silent
 * catch).
 *
 * @param {string} userId - profile_user_id
 * @returns {Promise<{enabled: boolean, error?: string}>}
 */
export const hydrateSharingFromDb = async (userId) => {
  try {
    const row = await getMyLiveLocation(userId);
    const enabled = !!row?.sharing_enabled;
    localStorage.setItem(SHARING_ENABLED_KEY, String(enabled));
    // Also return the stored location (raw — neighborhood, city, updated_at) so
    // the People pin's share sheet can paint the neighbourhood on first open
    // instead of starting blank and fetching it again (the blank-flash fix,
    // f/share-sheet-place). The sheet owns the freshness decision — it decides
    // whether this stored neighbourhood is fresh enough to show — so no freshness
    // gate lives here.
    return { enabled, location: row || null };
  } catch (err) {
    return {
      enabled: getLocationSharingEnabled(),
      location: null,
      error: err?.message || 'read_failed',
    };
  }
};
